<template>
    <div class="historico-container">
      <!-- Admin Summary Section -->
      <div v-if="userAdmin && portfolioDetails && orderedClosedPositions.length > 0" class="portfolio-summary-header admin-summary">
          <h4>Resumen de Totales (Admin)</h4>
          <ul class="portfolio-metrics">
              <li>
                  <span class="material-icons-outlined">account_balance_wallet</span>
                  Total Bº/Pº: <b>{{ formatCurrencyRight(totalProfitLoss, displayPortfolioCurrency) }}</b>
              </li>
              <li>
                  <span class="material-icons-outlined">receipt_long</span>
                  Total Comisiones Apertura: <b>{{ formatCurrencyRight(totalOpenFees, displayPortfolioCurrency) }}</b>
              </li>
              <li>
                  <span class="material-icons-outlined">receipt</span>
                  Total Comisiones Cierre: <b>{{ formatCurrencyRight(totalCloseFees, displayPortfolioCurrency) }}</b>
              </li>
          </ul>
      </div>
  
      <div v-if="loading" class="loading-indicator-gcn">Cargando histórico...</div>
      <div v-if="error" class="error-message-gcn">{{ error }}</div>
  
      <div class="table-responsive" v-if="!loading && !error && orderedClosedPositions.length > 0">
          <table class="table data-table history-table">
              <thead>
                  <tr>
                      <th v-for="col in displayColumns" :key="col.key" @click="col.sortable ? sortBy(col.key) : null" :class="{ 'sortable': col.sortable }" :title="getFullHeaderText(col.label)">
                          <div class="header-content">
                              <span class="header-text">{{ col.label }}</span>
                              <span v-if="col.sortable && sort.key === col.key" class="sort-icon">
                                  <span class="material-icons-outlined">{{ sort.order === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down' }}</span>
                              </span>
                          </div>
                      </th>
                  </tr>
              </thead>
              <tbody>
                  <tr v-for="pos in orderedClosedPositions" :key="pos.id" :class="{ 'profit-positive': pos.total > 0, 'profit-negative': pos.total < 0 }">
                      <!-- Ticker Cell -->
                      <td class="ticker-cell">
                           <div class="ticker-content-wrapper">
                               <div class="ticker-left">
                                   <b>{{ pos.ticker }}</b>
                                   <small v-if="pos.company_name">{{ pos.company_name }}</small>
                                   <small v-if="pos.entry_type_name">Tipo Entrada: {{ pos.entry_type_name }}</small>
                               </div>
                               <div class="ticker-right">
                                   <span v-if="pos.direction === 1" title="Alcista" class="direction-icon bullish material-icons-outlined">trending_up</span>
                                   <span v-else-if="pos.direction === 0 || pos.direction === false" title="Bajista" class="direction-icon bearish material-icons-outlined">trending_down</span>
                                   <span v-if="pos.instrument_type === 'STOCK'" title="Acción" class="instrument-icon material-icons-outlined">show_chart</span>
                                   <span v-else-if="pos.instrument_type === 'CFD'" title="CFD" class="instrument-icon material-icons-outlined">candlestick_chart</span>
                                   <small v-else-if="pos.instrument_type" class="instrument-type-text">{{ pos.instrument_type }}</small>
                                   <small class="currency">{{ pos.currency || '-' }}</small>
                                   <span v-if="pos.ticker_in_open_position_too" title="También en posiciones abiertas" class="material-icons-outlined open-position-indicator">work_history</span>
                               </div>
                           </div>
                      </td>
                      <!-- Instrument Number -->
                      <td class="number-cell">{{ pos.instrument_number }}</td>
                      <!-- In Price -->
                      <td class="number-cell">{{ formatPrice(pos.in_price, pos.stock_decimals) }}</td>
                      <!-- Open Fee -->
                      <td class="number-cell">{{ formatCurrencyRight(pos.open_fee, displayPortfolioCurrency) }}</td>
                      <!-- In Currency -->
                      <td class="number-cell">{{ pos.in_currency ? formatPrice(pos.in_currency, 2) : '-' }}</td>
                      <!-- Out Price -->
                      <td class="number-cell">{{ formatPrice(pos.user_out_price, pos.stock_decimals) }}</td>
                      <!-- Close Fee -->
                      <td class="number-cell">{{ formatCurrencyRight(pos.close_fee, displayPortfolioCurrency) }}</td>
                      <!-- Out Currency -->
                      <td class="number-cell">{{ pos.out_currency ? formatPrice(pos.out_currency, 2) : '-' }}</td>
                      <!-- In Date -->
                      <td>{{ formatDate(pos.in_formatted_date) }}</td>
                      <!-- Out Date -->
                      <td>{{ formatDate(pos.out_formatted_date) }}</td>
                      <!-- Support Date -->
                      <td>{{ pos.support_date ? formatDate(pos.support_date) : '-' }}</td>
                      <!-- Wave Number -->
                      <td class="number-cell">{{ pos.wave_number !== undefined ? pos.wave_number : '-' }}</td>
                      <!-- Total -->
                      <td :class="getTextClass(pos.total)" class="number-cell">
                          {{ formatCurrencyRight(pos.total, displayPortfolioCurrency) }}
                      </td>
                       <td class="notes-cell">
                           <span v-if="!pos.editingNotes" @dblclick="startEditNotes(pos)">{{ pos.notes || '-' }}</span>
                           <textarea v-else v-model="pos.editNotesValue" @blur="saveNotes(pos)" @keyup.enter="saveNotes(pos)" @keyup.esc="cancelEditNotes(pos)" class="form-control-gcn form-control-gcn-sm" :data-id="pos.id" ref="notesTextarea" v-focus-textarea></textarea>
                           <button v-if="!pos.editingNotes" class="btnGcn btnGcn-link btnGcn-sm btnGcn-edit-note" @click="startEditNotes(pos)" title="Editar Notas"><span class="material-icons-outlined">edit</span></button>
                          <button v-else class="btnGcn btnGcn-link btnGcn-sm btnGcn-save-note" @click="saveNotes(pos)" title="Guardar Notas"><span class="material-icons-outlined">save</span></button>
                      </td>
                      <td class="options-cell">
                          <a v-if="pos.chart_url_params" :href="buildChartUrl(pos.chart_url_params)" target="_blank" class="btnGcn btnGcn-link btnGcn-sm btnGcn-chart" title="Ver gráfico"><span class="material-icons-outlined">bar_chart</span></a>
                          <button class="btnGcn btnGcn-link btnGcn-sm btnGcn-graph" @click="openDynamicChart(pos.ticker)" title="Graficador Dinámico"><span class="material-icons-outlined">bar_chart</span></button>
                          <button class="btnGcn btnGcn-link btnGcn-sm btnGcn-danger btnGcn-delete" @click="confirmDelete(pos)" title="Eliminar Registro"><span class="material-icons-outlined">delete</span></button>
                      </td>
                  </tr>
              </tbody>
          </table>
      </div>
      <div v-else-if="!loading && !error && closedPositions.length === 0">
          <p>No hay posiciones cerradas en el histórico para esta cartera.</p>
      </div>
    </div>
  </template>
  
  <script>
  import apiService, { parseDate, formatCurrency } from './apiService';
  
  export default {
    name: 'Historico',
    props: {
      portfolioId: { type: String, required: true },
    },
    directives: {
        focusTextarea: {
            inserted(el) {
                el.focus();
            }
        }
    },
    data() {
      return {
        loading: false,
        error: null,
        closedPositions: [],
        portfolioDetails: null, // To store portfolio name, currency etc.
        userAdmin: false, // To control admin-specific views
        isSubmitting: false, // For note saving/delete operations
        sort: {
            key: 'out_formatted_date', // Default sort key
            order: 'desc', // Default sort order ('asc' or 'desc')
        },
        displayColumns: [
            { key: 'ticker', label: 'Ticker', sortable: true },
            { key: 'instrument_number', label: 'Acc.', sortable: true, class: 'number-cell' },
            { key: 'in_price', label: 'P. Ent.', sortable: true, class: 'number-cell' },
            { key: 'open_fee', label: 'Com. Ap.', sortable: true, class: 'number-cell' },
            { key: 'in_currency', label: 'Div. Ent.', sortable: true, class: 'number-cell' },
            { key: 'user_out_price', label: 'P. Sal.', sortable: true, class: 'number-cell' },
            { key: 'close_fee', label: 'Com. Ci.', sortable: true, class: 'number-cell' },
            { key: 'out_currency', label: 'Div. Sal.', sortable: true, class: 'number-cell' },
            { key: 'in_formatted_date', label: 'F. Ent.', sortable: true },
            { key: 'out_formatted_date', label: 'F. Sal.', sortable: true },
            { key: 'support_date', label: 'F. Sop.', sortable: true },
            { key: 'wave_number', label: 'Nº Onda', sortable: true, class: 'number-cell' },
            { key: 'total', label: 'Total', sortable: true, class: 'number-cell' },
            { key: 'notes', label: 'Notas', sortable: false, class: 'notes-cell' },
            { key: 'actions', label: 'Opc.', sortable: false, class: 'options-cell' },
        ],
      };
    },
    computed: {
        orderedClosedPositions() {
            if (!this.closedPositions) return [];
            return [...this.closedPositions].sort((a, b) => {
                let valA = a[this.sort.key];
                let valB = b[this.sort.key];

                // Handle date sorting (assuming dates are YYYY-MM-DD or parseable by Date)
                if (this.sort.key.includes('_date') || this.sort.key.includes('Date')) {
                    valA = valA ? new Date(parseDate(valA)).getTime() : 0;
                    valB = valB ? new Date(parseDate(valB)).getTime() : 0;
                }

                // Handle numeric sorting for price, fees, total etc.
                if (typeof valA === 'string' && !isNaN(parseFloat(valA)) && typeof valB === 'string' && !isNaN(parseFloat(valB))) {
                    valA = parseFloat(valA);
                    valB = parseFloat(valB);
                } else if (typeof valA === 'number' && typeof valB === 'number') {
                    // already numbers
                } else { // Fallback to string comparison for other types or mixed types
                    valA = String(valA || '').toLowerCase();
                    valB = String(valB || '').toLowerCase();
                }

                let comparison = 0;
                if (valA > valB) {
                    comparison = 1;
                } else if (valA < valB) {
                    comparison = -1;
                }
                return this.sort.order === 'desc' ? comparison * -1 : comparison;
            });
        },
        displayPortfolioCurrency() {
            return (this.portfolioDetails && this.portfolioDetails.currency) ? this.portfolioDetails.currency : 'EUR';
        },
        totalProfitLoss() {
            if (!this.userAdmin) return 0;
            return this.closedPositions.reduce((sum, pos) => sum + (pos.total || 0), 0);
        },
        totalOpenFees() {
            if (!this.userAdmin) return 0;
            return this.closedPositions.reduce((sum, pos) => sum + (pos.open_fee || 0), 0);
        },
        totalCloseFees() {
            if (!this.userAdmin) return 0;
            return this.closedPositions.reduce((sum, pos) => sum + (pos.close_fee || 0), 0);
        }
    },
    methods: {
       // --- Anglo Number Formatting Helpers ---
       formatNumberAnglo(value, decimals = 2) {
         if (value === null || value === undefined || value === '') return '-';
         const num = typeof value === 'number' ? value : Number(value);
         if (isNaN(num)) return '-';
         const absValue = Math.abs(num);
         const parts = absValue.toFixed(decimals).split('.');
         const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
         const decimalPart = parts[1];
         return decimalPart ? `${integerPart}.${decimalPart}` : integerPart;
       },
       formatCurrency(value, currency = 'EUR') {
         return this.formatCurrencyRight(value, currency);
       },
       formatCurrencyRight(value, currency = 'EUR') {
          if (typeof value !== 'number') return '-';
          const isNegative = value < 0;
          const formattedNumber = this.formatNumberAnglo(Math.abs(value), 2);
          const currencySymbol = currency === 'EUR' ? '€' : currency;
          
          return isNegative ? `-${formattedNumber}${currencySymbol}` : `${formattedNumber}${currencySymbol}`;
       },
       formatDate(dateString) {
          if (!dateString) return '-';
          const date = parseDate(dateString); // Handles d-m-Y or Y-m-d
          return date ? date.toLocaleDateString('es-ES', { year: '2-digit', month: '2-digit', day: '2-digit' }) : (dateString || '-');
      },
      formatPrice(value, decimals) {
        const num = typeof value === 'number' ? value : Number(value);
        if (isNaN(num)) return '-';
        const numDecimals = (typeof decimals === 'number' && decimals >= 0) ? decimals : 2;
        return this.formatNumberAnglo(num, numDecimals);
      },
       getTextClass(value) {
          if (typeof value !== 'number') return '';
          return value >= 0 ? 'text-success' : 'text-danger';
      },
      async fetchAllData() {
        if (!this.portfolioId) return;
        this.loading = true;
        this.error = null;
        try {
            // Fetch portfolio details (name, currency)
            // Assuming apiService.getPortfolio and apiService.checkUserAdmin exist
            const [positions, portfolioData, isAdmin] = await Promise.all([
                apiService.listClosedPositions(this.portfolioId),
                apiService.getPortfolio(this.portfolioId).catch(e => { console.warn("Could not fetch portfolio details:", e); return null; }),
                apiService.checkUserAdmin().catch(e => { console.warn("Could not check admin status:", e); return false; })
            ]);

            this.closedPositions = positions.map(p => ({
                ...p,
                editingNotes: false,
                editNotesValue: p.notes,
                // Ensure numeric fields are numbers for sorting and calculation
                instrument_number: parseFloat(p.instrument_number) || 0,
                in_price: parseFloat(p.in_price) || 0,
                open_fee: parseFloat(p.open_fee) || 0,
                user_out_price: parseFloat(p.user_out_price) || 0,
                close_fee: parseFloat(p.close_fee) || 0,
                total: parseFloat(p.total) || 0,
                in_currency: p.in_currency ? parseFloat(p.in_currency) : null,
                out_currency: p.out_currency ? parseFloat(p.out_currency) : null,
            }));

            if (portfolioData) {
                this.portfolioDetails = portfolioData;
            }
            this.userAdmin = isAdmin;

        } catch (err) {
          this.error = `Error cargando datos del histórico: ${err.message}`;
          this.closedPositions = [];
          this.portfolioDetails = null;
          this.userAdmin = false;
        } finally {
          this.loading = false;
        }
      },
       startEditNotes(position) {
           // Ensure only one row is in edit mode for notes
           this.closedPositions.forEach(p => { if (p.id !== position.id) p.editingNotes = false; });
           position.editNotesValue = position.notes;
           position.editingNotes = true;
           // $nextTick usage for focus is better in a directive or a more specific ref
       },
       cancelEditNotes(position) { position.editingNotes = false; },
       async saveNotes(position) {
          if (position.notes === position.editNotesValue && position.editingNotes) { // only close if value is same AND it was editing
              position.editingNotes = false;
              return;
          }
          if (!position.editingNotes) return; // Do nothing if not in edit mode (e.g. blur triggered after save)

          this.isSubmitting = true;
          try {
              const updateData = { closed_position_id: position.id, note: position.editNotesValue };
              // Assuming API returns the updated position or confirms success
              const updatedPositionFromServer = await apiService.updateClosedPositionNote(this.portfolioId, updateData);

               const index = this.closedPositions.findIndex(p => p.id === position.id);
               if (index !== -1) {
                   // Prefer using the returned data from server if available and complete
                   if (updatedPositionFromServer && typeof updatedPositionFromServer === 'object') {
                       this.$set(this.closedPositions, index, {
                           ...this.closedPositions[index], // keep other local potentially unsaved state if any
                           ...updatedPositionFromServer, // override with server truth
                           editingNotes: false, // ensure edit mode is off
                           editNotesValue: updatedPositionFromServer.notes, // ensure editNotesValue is also updated
                       });
                   } else { // Fallback to local update if API response is minimal (e.g. just status)
                       this.$set(this.closedPositions[index], 'notes', position.editNotesValue);
                       this.$set(this.closedPositions[index], 'editingNotes', false);
                   }
               } else {
                   await this.fetchAllData(); // Fallback: full refresh if position not found (should not happen)
               }
          } catch (err) {
              console.error("Error saving note:", err);
              alert(`Error guardando nota: ${err.message}`); // Consider a more user-friendly notification system
          } finally {
              this.isSubmitting = false;
              // Ensure editingNotes is false regardless of success/failure if it was true
              if (position.editingNotes) {
                  position.editingNotes = false;
              }
          }
      },
      async confirmDelete(positionToDelete) {
          if (confirm(`¿Está seguro de que desea eliminar permanentemente la entrada del histórico (${positionToDelete.ticker})? Esta acción no se puede deshacer.`)) {
              this.isSubmitting = true; this.error = null;
              try {
                  await apiService.deleteClosedPosition(this.portfolioId, positionToDelete.id);
                  // Remove the item from the local list for responsiveness
                  this.closedPositions = this.closedPositions.filter(p => p.id !== positionToDelete.id);
                  // Optionally, show a success message
              } catch (err) {
                  this.error = `Error al eliminar del histórico: ${err.message}`;
                  // Optionally, show an error message to the user
              } finally {
                  this.isSubmitting = false;
              }
          }
      },
      sortBy(key) {
          if (this.sort.key === key) {
              this.sort.order = this.sort.order === 'asc' ? 'desc' : 'asc';
          } else {
              this.sort.key = key;
              this.sort.order = 'asc'; // Default to ascending when changing column
          }
      },
      buildChartUrl(params) {
          // Assuming params is an object like {s: 'TICKER', d: '1'}
          // Or if it's just the ticker string:
          if (typeof params === 'string') return `/apps/chart?s=${params}&d=1`;
          const baseUrl = '/apps/chart'; // Or your actual chart app path
          const query = new URLSearchParams(params).toString();
          return `${baseUrl}?${query}`;
      },
      openDynamicChart(ticker) {
          const url = this.$detectEnvironment() + "/app/graficador-dinamico/ticker/" + ticker;
          window.open(url, "_blank");
      },
      getFullHeaderText(abbreviatedLabel) {
          const fullTextMap = {
              'Ticker': 'Ticker',
              'Acc.': 'Acciones',
              'P. Ent.': 'Precio de Entrada',
              'Com. Ap.': 'Comisión Apertura',
              'Div. Ent.': 'Divisa Entrada',
              'P. Sal.': 'Precio de Salida',
              'Com. Ci.': 'Comisión Cierre',
              'Div. Sal.': 'Divisa Salida',
              'F. Ent.': 'Fecha de Entrada',
              'F. Sal.': 'Fecha de Salida',
              'F. Sop.': 'Fecha de Soporte',
              'Nº Onda': 'Número de Onda',
              'Total': 'Total',
              'Notas': 'Notas',
              'Opc.': 'Opciones'
          };
          return fullTextMap[abbreviatedLabel] || abbreviatedLabel;
      }
    },
    watch: {
      portfolioId: {
        immediate: true,
        handler(newId) {
          if (newId) { this.fetchAllData(); }
          else {
            this.closedPositions = [];
            this.error = null;
            this.portfolioDetails = null;
            this.userAdmin = false;
          }
        },
      },
    },
  };
  </script>
  
  <style scoped>
  /* --- Colors from App Theme --- */
  .historico-container {
    color: var(--color-text-primary);
    font-size: 0.92rem;
    padding: 0 10px;
  }

  .material-icons-outlined {
    font-size: 16px;
    vertical-align: middle;
    line-height: 1;
    margin-right: 3px;
  }

  .btnGcn-sm .material-icons-outlined, 
  .options-cell .material-icons-outlined, 
  .notes-cell .material-icons-outlined {
    font-size: 18px;
    margin-right: 0;
  }

  .ticker-right .material-icons-outlined {
    font-size: 18px;
    margin-right: 0;
  }

  .admin-summary .material-icons-outlined {
    margin-right: 8px;
    color: var(--color-highlight);
  }

  /* --- Portfolio Summary Header Styles --- */
  .portfolio-summary-header {
    background-color: var(--color-surface);
    padding: 12px 18px;
    margin-bottom: 20px;
    border-radius: 6px;
    box-shadow: 0 1px 3px var(--color-outline);
    border: 1px solid var(--color-outline);
  }

  .portfolio-summary-header h4 {
    margin-top: 0;
    margin-bottom: 10px;
    color: var(--color-text-secondary);
    font-size: 0.95em;
  }

  .portfolio-metrics {
    list-style: none;
    padding: 0;
    margin: 0;
    font-size: 0.9em;
    color: var(--color-text-secondary);
  }

  .portfolio-metrics li {
    margin-bottom: 5px;
    display: flex;
    align-items: center;
  }

  .portfolio-metrics li:last-child { 
    margin-bottom: 0; 
  }

  .portfolio-metrics b {
    color: var(--color-text-primary);
    font-weight: 600;
    margin: 0 3px;
  }

  .loading-indicator-gcn, .error-message-gcn {
    color: var(--color-text-secondary);
    text-align: center;
    padding: 15px;
    font-size: 0.9em;
    border-radius: 4px;
    margin-bottom: 12px;
  }

  .error-message-gcn {
    color: var(--color-error);
    background-color: var(--color-active-surface);
  }

  .table-responsive {
    overflow-x: auto;
    margin-top: 12px;
    border-radius: 4px;
    box-shadow: 0 2px 8px var(--color-outline);
    margin-bottom: 20px;
  }

  .table.data-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.85em;
  }

  .data-table th, .data-table td {
    border: 1px solid var(--color-outline);
    padding: 6px 8px;
    text-align: center;
    vertical-align: middle;
    color: var(--color-text-primary);
    line-height: 1.3;
  }

  .data-table th {
    background-color: var(--color-surface);
    font-weight: 600;
    white-space: normal;
    word-wrap: break-word;
    color: var(--color-text-secondary);
    font-size: 0.75em;
    text-align: center;
    padding: 3px 4px;
    line-height: 1.1;
    max-width: 90px;
    min-width: 60px;
  }

  .data-table th.sortable {
    cursor: pointer;
    user-select: none;
    transition: background-color 0.2s ease;
  }

  .data-table th.sortable:hover {
    background-color: var(--color-active-surface);
  }

  .data-table th.sortable:active {
    background-color: var(--color-outline);
  }

  .header-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    position: relative;
    min-height: 20px;
  }

  .header-text {
    text-align: center;
  }

  .sort-icon {
    position: absolute;
    top: 50%;
    right: 2px;
    transform: translateY(-50%);
    line-height: 1;
  }

  .sort-icon .material-icons-outlined {
    font-size: 16px;
    color: var(--color-highlight);
    display: block;
  }

  /* Removed alternating row colors per user preference */
  /* .data-table tbody tr:nth-child(even) {
    background-color: var(--color-active-surface);
  } */

  .data-table tbody tr:hover {
    background-color: var(--color-active-surface);
  }

  /* Row background colors based on profit/loss */
  .data-table tbody tr.profit-positive {
    background-color: rgba(var(--color-success-rgb, 40, 167, 69), 0.15);
  }

  .data-table tbody tr.profit-negative {
    background-color: rgba(var(--color-error-rgb, 220, 53, 69), 0.15);
  }

  .data-table tbody tr.profit-positive:hover {
    background-color: rgba(var(--color-success-rgb, 40, 167, 69), 0.15);
  }

  .data-table tbody tr.profit-negative:hover {
    background-color: rgba(var(--color-error-rgb, 220, 53, 69), 0.15);
  }

  /* --- Ticker Cell Styling --- */
  .ticker-cell {
    font-weight: 600;
    vertical-align: top;
    text-align: left;
  }

  /* Override centering specifically for ticker cells in table body */
  .data-table tbody .ticker-cell {
    text-align: left;
  }

  .ticker-content-wrapper { 
    display: flex; 
    justify-content: space-between; 
    align-items: center; 
    width: 100%; 
  }

  .ticker-left { 
    flex-grow: 1; 
    margin-right: 8px; 
  }

  .ticker-left b { 
    display: block; 
    font-size: 1.05em; 
    color: var(--color-text-primary); 
  }

  .ticker-left small { 
    display: block; 
    color: var(--color-text-secondary); 
    font-size: 0.85em; 
    line-height: 1.2; 
    font-weight: 400; 
  }

  .ticker-right { 
    display: flex; 
    flex-direction: column; 
    align-items: flex-end; 
    flex-shrink: 0; 
    text-align: right; 
  }

  .ticker-right .direction-icon, 
  .ticker-right .instrument-icon, 
  .ticker-right .instrument-type-text { 
    display: block; 
    margin-bottom: 1px; 
    width: auto; 
    text-align: right; 
  }

  .direction-icon.bullish {
    color: var(--color-success);
    text-shadow: 0 0 6px var(--color-success);
  }

  .direction-icon.bearish {
    color: var(--color-error);
    text-shadow: 0 0 6px var(--color-error);
  }

  .instrument-icon {
    color: var(--color-text-secondary);
    font-size: 1em;
  }

  .instrument-type-text { 
    font-size: 0.8em; 
    color: var(--color-text-secondary); 
  }

  .ticker-right small.currency { 
    font-size: 0.8em; 
    color: var(--color-text-secondary); 
    margin-top: 2px; 
  }

  .open-position-indicator { 
    color: var(--color-warning); 
    font-size: 16px !important; 
  }

  /* --- Notes Cell Styling --- */
  .notes-cell {
    min-width: 150px;
    white-space: pre-wrap;
    font-size: 0.9em;
  }

  .notes-cell span:not(.material-icons-outlined) { 
    cursor: text; 
  }

  .notes-cell textarea {
    width: 100%;
    min-height: 50px;
    font-size: 0.9em;
    padding: 4px;
    box-sizing: border-box;
    border-radius: 3px;
    border: 1px solid var(--color-outline);
    background-color: var(--color-surface);
    color: var(--color-text-primary);
  }

  .notes-cell textarea:focus {
    border-color: var(--color-highlight);
    outline: none;
    box-shadow: 0 0 0 2px var(--color-active-surface);
  }

  .notes-cell .btnGcn-link {
    padding: 0; 
    width: 20px; 
    height: 20px;
    font-size: 14px;
    vertical-align: top;
    color: var(--color-text-secondary);
    background-color: transparent;
    border: none;
    border-radius: 4px;
    display: inline-flex; 
    align-items: center; 
    justify-content: center;
    margin-left: 4px;
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .notes-cell .btnGcn-link:hover {
    color: var(--color-text-primary);
    background-color: var(--color-surface);
    transform: scale(1.1);
  }

  .notes-cell .btnGcn-link:active {
    transform: scale(1.05);
    background-color: var(--color-active-surface);
  }

  .notes-cell .btnGcn-link .material-icons-outlined { 
    margin-right: 0; 
  }

  /* --- Options Cell Styling --- */
  .options-cell { 
    white-space: nowrap; 
    text-align: center; 
  }

  .options-cell .btnGcn-link {
    padding: 0; 
    width: 24px; 
    height: 24px;
    font-size: 16px;
    text-decoration: none; 
    vertical-align: middle;
    color: var(--color-text-secondary); 
    background-color: transparent;
    border: none; 
    border-radius: 4px;
    display: inline-flex; 
    align-items: center; 
    justify-content: center;
    margin: 0 1px;
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .options-cell .btnGcn-link:hover {
    color: var(--color-text-primary);
    background-color: var(--color-surface);
    transform: scale(1.1);
  }

  .options-cell .btnGcn-link:active {
    transform: scale(1.05);
    background-color: var(--color-active-surface);
  }

  .options-cell .btnGcn-link:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--color-active-surface);
  }

  .options-cell .btnGcn-link.btnGcn-danger:hover, 
  .options-cell .btnGcn-link.btnGcn-delete:hover {
    color: var(--color-text-on-highlight);
    background-color: var(--color-error);
  }

  .options-cell .btnGcn-link.btnGcn-danger, 
  .options-cell .btnGcn-link.btnGcn-delete {
    color: var(--color-error);
  }

  .options-cell .btnGcn-link.btnGcn-chart {
    color: var(--color-highlight);
  }

  .options-cell .btnGcn-link.btnGcn-chart:hover {
    color: var(--color-text-on-highlight);
    background-color: var(--color-highlight);
  }

  .options-cell .btnGcn-link.btnGcn-graph {
    color: var(--color-highlight);
  }

  .options-cell .btnGcn-link.btnGcn-graph:hover {
    color: var(--color-text-on-highlight);
    background-color: var(--color-highlight);
  }

  .options-cell .btnGcn-link.disabled {
    color: var(--color-text-inactive);
    cursor: not-allowed;
    pointer-events: none;
    opacity: 0.3;
    transition: none;
  }

  .options-cell .btnGcn-link.disabled:hover,
  .options-cell .btnGcn-link.disabled:focus,
  .options-cell .btnGcn-link.disabled:active {
    background-color: transparent !important;
    color: var(--color-text-inactive) !important;
    transform: none !important;
    box-shadow: none !important;
    opacity: 0.3 !important;
  }

  /* --- Text classes for profit/loss --- */
  .text-success, 
  tr.profit-positive td:last-of-type { 
    color: var(--color-success); 
    font-weight: 500; 
  }

  .text-danger, 
  tr.profit-negative td:last-of-type { 
    color: var(--color-error); 
    font-weight: 500; 
  }

  td.text-success small, 
  td.text-danger small { 
    font-weight: normal; 
    color: var(--color-text-secondary); 
  }

  /* --- Numeric cell alignment --- */
  .data-table td.number-cell, 
  .data-table th.number-cell {
    text-align: center;
    padding-right: 8px;
  }

  .data-table th.number-cell { 
    text-align: center; 
  }

  /* --- Total column specific styles --- */
  .data-table td:nth-child(13),
  .data-table th:nth-child(13) {
    white-space: nowrap;
    min-width: fit-content;
  }

  /* --- Responsive Design --- */
  @media (max-width: 992px) {
    .data-table th, .data-table td {
      padding: 6px 8px;
      font-size: 0.8em;
    }
    
    .notes-cell { 
      min-width: 120px; 
    }
    
    .ticker-left b { 
      font-size: 1em; 
    }
  }

  @media (max-width: 768px) {
    .data-table th, .data-table td {
      padding: 4px 6px;
      font-size: 0.8em;
    }
    
    .options-cell .btnGcn-link {
      width: 22px;
      height: 22px;
      font-size: 14px;
    }
    
    .options-cell .btnGcn-link .material-icons-outlined,
    .notes-cell .btnGcn-link .material-icons-outlined {
      font-size: 14px;
    }
    
    .ticker-right .material-icons-outlined {
      font-size: 16px;
    }
  }
  </style>